import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useMount } from '@croquiscom/monolith';
import { getAppVersion } from '@croquiscom/web2app';
import { ThemeProvider, ZdsTheme } from '@croquiscom/zds';
import { Global } from '@emotion/react';
import * as Sentry from '@sentry/nextjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Stage from 'components/toolbox/stage';
import useBrowserEffect from 'hooks/useBrowserEffect';
import { NextPage } from 'next';
import { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import { Router, useRouter } from 'next/router';

import { AppConfigProvider } from '@common/app-config';
import { Preconnect, PreloadFont } from '@common/components';
import { isZigzag, UserAgentProvider } from '@common/device-manager';
import { useClarityInitializeService } from '@common/hooks';
import { convertToPDPDeepLink } from '@common/pdp';
import { font, reset } from '@common/styles';
import { getFingerPrint, initializeFingerPrint, setUtmCookies } from '@common/utils';
import { LikeSubject, useLikeObserve } from '@widgets/like-product';
import { ProductCardProvider, VerticalProductCardContextType } from '@common/product-card';
import { useClearAuthInfo } from '@domains/auth-common';

import { globalStyle } from '../styles/globalStyle';

import config from 'util/config';
import { TrackerProvider } from 'util/Tracker';

import 'styles/zds.css';
import 'styles/zds-hotfix.css';
import '@common/carousel/swiper/styles.css';
import 'styles/event-modules.css';
import 'react-day-picker/style.css';

if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  initVConsole();
}

export type NextPageAddon<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
  getSentrySendData?: (props: P) => {};
};

export type AppPropsWithLayout = AppProps & {
  Component: NextPageAddon;
  pageProps: any;
};

// 현재는 가로형, 세로형 링크의 동작이 같기 때문에 한벌로 쓰고, 이후 Vertical, Horizontal로 분기가 될 수 있습니다.
const productCardContextValue: VerticalProductCardContextType = {
  href: (item) =>
    convertToPDPDeepLink({
      browsing_type: item.browsingType,
      catalog_product_id: item.productId,
      product_url: item.productUrl,
      shop_id: item.shopId,
      shop_product_no: item.shopProductNo,
    }),
};

export const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  const client = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 1000 * 60 * 2,
        },
      },
    });
  }, []);

  const router = useRouter();
  const [theme, setTheme] = useState('');
  const getLayout = Component.getLayout ?? ((page) => page);
  // 서버에서 user-agent는 CloudFront에 의해 오염된 상태여서 클라이언트에서는 클라이언트 것을 사용한다
  const user_agent: string = (typeof navigator === 'undefined' ? pageProps.user_agent : navigator.userAgent) ?? '';

  const zigzagUuid = null;
  const isZigzagApp = /(zigzag)/i.test(user_agent);

  const handlePageLoad = useRef(() => {
    document.body.className += 'active';

    const scope = Sentry.getCurrentScope();
    scope.addEventProcessor((event) => {
      event.extra = {
        ...(Component.getSentrySendData && { ...Component.getSentrySendData(pageProps) }),
      };

      return event;
    });
    const zigzag_version = getAppVersion(navigator.userAgent);
    if (zigzag_version) {
      scope.setTag('zigzag_version', zigzag_version);
    }
  });

  useClearAuthInfo();
  useClarityInitializeService(config.clarity_project_id);

  useMount(() => {
    setUtmCookies(router.query);

    handlePageLoad.current();
  });

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();
  }, []);

  useBrowserEffect(() => {
    const html = document.querySelector('html')!;
    const theme = html.dataset.theme || 'light';
    setTheme(theme);
  }, []);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      isZigzag() === false &&
      window.location.hostname.startsWith('store.') &&
      window.location.hostname.includes('zigzag.kr')
    ) {
      //MEMO: IOS 운영에서만 퍼포먼스가 있어 실 웹뷰 확인을 위한 해당 페이지 redirect 제외처리
      const is_card_matching = window.location.href.endsWith('card-matching');
      if (is_card_matching) {
        return;
      }

      window.location.replace(getRedirectUrl(window.location.href));
    }
  }, []);

  useBrowserEffect(() => {
    const existing_fingerprint = getFingerPrint();

    if (!existing_fingerprint) {
      initializeFingerPrint();
    }
  }, []);

  return (
    <UserAgentProvider initialUserAgent={user_agent}>
      <AppConfigProvider
        zigzagUuid={zigzagUuid}
        isZigzagApp={isZigzagApp}
        croquisClientNavigation={pageProps.croquis_client_navigation}
      >
        <TrackerProvider user_agent={user_agent}>
          <Sentry.ErrorBoundary>
            <LikeSubject>
              <Head>
                <meta
                  name='viewport'
                  content='width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no,viewport-fit=cover'
                />
              </Head>
              <Preconnect />
              <PreloadFont />
              <Global styles={globalStyle} />
              <Global styles={reset} />
              <Global styles={font} />
              <ProductCardProvider
                useLikeObserver={useLikeObserve}
                verticalProductCard={productCardContextValue}
                horizontalProductCard={productCardContextValue}
              >
                <ThemeProvider name={theme as ZdsTheme['name']}>
                  <QueryClientProvider client={client}>{getLayout(<Component {...pageProps} />)}</QueryClientProvider>
                  <Stage />
                </ThemeProvider>
              </ProductCardProvider>
            </LikeSubject>
          </Sentry.ErrorBoundary>
        </TrackerProvider>
      </AppConfigProvider>
    </UserAgentProvider>
  );
};

MyApp.getInitialProps = async ({ Component, ctx, router }: AppContext) => {
  // Component.getInitialProps 전에 setHeader 호출해야함
  setFrameOptions(ctx);

  let initial_page_props = {};
  if (Component.getInitialProps) {
    initial_page_props = await Component.getInitialProps(ctx);
  }

  const user_agent = ctx.req?.headers['user-agent'] ?? '';

  const pageProps = {
    ...initial_page_props,
    user_agent,
    is_pdp: isPdp(router),
    // Croquis-Client-Navigation가 croquis-client-navigation로 headers에 오기 때문
    croquis_client_navigation: ctx.req?.headers['croquis-client-navigation'] ?? null,
  };

  return { pageProps };
};

export default MyApp;

function isPdp(router: Router) {
  try {
    return router.asPath.includes('/catalog/products/') || router.asPath.includes('/raffle/detail/');
  } catch (error) {
    return false;
  }
}

function setFrameOptions({ req, res }: AppContext['ctx']) {
  if (!req || !res) {
    return;
  }
  try {
    const { hostname } = new URL(req.headers.referer || '');
    if (hostname === 'localhost' || hostname.endsWith('zigzag.kr') || hostname.endsWith('kakaostyle.com')) {
      return;
    } else {
      res.setHeader('X-Frame-Options', 'DENY');
    }
  } catch (e) {
    res.setHeader('X-Frame-Options', 'DENY');
  }
}

function getRedirectUrl(originUrl: string): string {
  const origin_url_controller = new URL(originUrl);
  if (origin_url_controller.host.includes('-dev-')) {
    origin_url_controller.host =
      'zigzag-web' + origin_url_controller.host.slice(origin_url_controller.host.indexOf('-dev-'));
  }
  origin_url_controller.host = origin_url_controller.host.replace('store.', '');
  // path에 /app이 있을 경우 제거
  if (origin_url_controller.pathname.includes('/app')) {
    origin_url_controller.pathname = origin_url_controller.pathname.replace('/app', '');
  }

  const redirect_url = origin_url_controller.toString();
  return redirect_url;
}
async function initVConsole() {
  try {
    const vConsole = (await import('vconsole')).default;
    new vConsole({
      onReady: () => {
        const button = document.querySelector('.vc-switch') as HTMLElement;
        button.style.position = 'fixed';
        button.style.bottom = '50px';
      },
    });
  } catch (error) {
    console.error(error);
  }
}
